import React, { useState } from 'react';
import { UploadCloud, Image as ImageIcon, CheckCircle2, X, AlertCircle } from 'lucide-react';
import { uploadNewsImage } from '../../../services/newsNewsletterApi';

export default function ImageUploader({
  value = null, // { url, caption, altText }
  onChange,
  disabled = false,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [variants, setVariants] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size (< 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Le fichier dépasse la taille maximale autorisée (5 Mo).");
      return;
    }

    // Check type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError("Format invalide. Seuls JPEG, PNG et WebP sont acceptés.");
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const res = await uploadNewsImage(file);
      if (res?.success && res.data) {
        setVariants(res.data);
        // Default to the optimized cover version or original
        const chosenUrl = res.data.cover?.url || res.data.original?.url;
        onChange?.({
          url: chosenUrl,
          caption: value?.caption || file.name.replace(/\.[^/.]+$/, ''),
          altText: value?.altText || file.name.replace(/\.[^/.]+$/, ''),
          variants: res.data,
        });
      }
    } catch (err) {
      setUploadError(err.message || "Échec de l'upload de l'image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setVariants(null);
    setUploadError(null);
    onChange?.(null);
  };

  return (
    <div className="image-uploader-card">
      {value?.url ? (
        <div className="image-preview-wrapper">
          <img src={value.url} alt={value.altText || 'Aperçu'} className="image-preview-img" />
          <div className="image-preview-actions">
            {!disabled && (
              <button
                type="button"
                className="image-remove-btn"
                onClick={handleRemove}
                title="Supprimer cette image"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {variants && (
            <div className="image-variants-pills">
              <span className="badge-pill variant">Sharp WebP</span>
              <span className="badge-pill">Cover 1200x630</span>
              <span className="badge-pill">Thumb 400x250</span>
              <span className="badge-pill">Email 600x315</span>
            </div>
          )}
        </div>
      ) : (
        <label className={`image-dropzone ${disabled ? 'disabled' : ''}`}>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            disabled={disabled || isUploading}
            style={{ display: 'none' }}
          />
          {isUploading ? (
            <div className="image-uploading-state">
              <div className="spinner-border text-primary" role="status" />
              <span>Optimisation Sharp WebP en cours...</span>
            </div>
          ) : (
            <div className="image-dropzone-content">
              <UploadCloud size={32} className="image-dropzone-icon" />
              <div className="image-dropzone-title">
                Cliquez ou glissez une image de couverture
              </div>
              <div className="image-dropzone-hint">
                JPEG, PNG ou WebP — Max 5 Mo (génération automatique des versions Cover, Vignette & Newsletter)
              </div>
            </div>
          )}
        </label>
      )}

      {uploadError && (
        <div className="image-upload-error">
          <AlertCircle size={15} />
          <span>{uploadError}</span>
        </div>
      )}
    </div>
  );
}

